/**
 * intri.ai API client
 *
 * In dev: Vite proxy forwards /api → api.intri.ai
 * In prod: VITE_API_URL env var or same-origin
 */

const API_BASE = import.meta.env.VITE_API_URL ?? "";

const REFRESH_TOKEN_KEY = "intri_refresh_token";

// ── Token storage ──
// Access token: in-memory only (short-lived, no persistence needed)
// Refresh token: localStorage (survives page refresh)

let accessToken: string | null = null;
let onAuthExpired: (() => void) | null = null;

export function setTokens(access: string, refresh: string) {
  accessToken = access;
  try {
    localStorage.setItem(REFRESH_TOKEN_KEY, refresh);
  } catch {
    // Private browsing or storage full — refresh token stays in-memory only
  }
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function clearTokens() {
  accessToken = null;
  try {
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  } catch {
    // Ignore
  }
}

export function setOnAuthExpired(cb: () => void) {
  onAuthExpired = cb;
}

/**
 * Attempt to restore session from a persisted refresh token.
 * Returns true if session was restored, false otherwise.
 */
export async function tryRestoreSession(): Promise<boolean> {
  let storedRefresh: string | null = null;
  try {
    storedRefresh = localStorage.getItem(REFRESH_TOKEN_KEY);
  } catch {
    return false;
  }

  if (!storedRefresh) return false;

  try {
    const res = await fetch(`${API_BASE}/api/v1/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: storedRefresh }),
    });

    if (!res.ok) {
      // Refresh token expired or invalid — clear it
      clearTokens();
      return false;
    }

    const data: TokenResponse = await res.json();
    setTokens(data.access_token, data.refresh_token);
    return true;
  } catch {
    // Network error — don't clear tokens, might be temporary
    return false;
  }
}

// ── HTTP helpers ──

async function refreshAccessToken(): Promise<boolean> {
  let storedRefresh: string | null = null;
  try {
    storedRefresh = localStorage.getItem(REFRESH_TOKEN_KEY);
  } catch {
    // Fall through
  }

  if (!storedRefresh) return false;

  try {
    const res = await fetch(`${API_BASE}/api/v1/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: storedRefresh }),
    });
    if (!res.ok) return false;
    const data: TokenResponse = await res.json();
    setTokens(data.access_token, data.refresh_token);
    return true;
  } catch {
    return false;
  }
}

async function request<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const url = `${API_BASE}${path}`;

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options.headers as Record<string, string>),
  };

  if (accessToken) {
    headers["Authorization"] = `Bearer ${accessToken}`;
  }

  let res = await fetch(url, { ...options, headers });

  // If 401, attempt token refresh once
  if (res.status === 401) {
    const refreshed = await refreshAccessToken();
    if (refreshed && accessToken) {
      headers["Authorization"] = `Bearer ${accessToken}`;
      res = await fetch(url, { ...options, headers });
    }
  }

  if (res.status === 401) {
    clearTokens();
    onAuthExpired?.();
    throw new ApiError("Session expired", 401);
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(body.detail ?? res.statusText, res.status);
  }

  return res.json() as Promise<T>;
}

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
    this.name = "ApiError";
  }
}

// ── API methods ──

export interface TokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
}

export interface UserResponse {
  id: string;
  email: string;
  display_name: string | null;
  plan: string;
  created_at: string;
}

export interface Project {
  id: string;
  name: string;
  description: string | null;
  created_at: string;
  updated_at: string;
}

export interface Session {
  id: string;
  project_id: string;
  title: string | null;
  created_at: string;
  ended_at: string | null;
}

export interface MessageResponse {
  id: string;
  role: string;
  content: string;
  widget_json: Record<string, unknown> | null;
  activity_log: Record<string, unknown> | Array<Record<string, unknown>> | null;
  token_count: number | null;
  created_at: string;
}

export interface SessionDetail extends Session {
  messages: MessageResponse[];
}

export interface ProjectMessage {
  id: string;
  session_id: string;
  role: string;
  content: string;
  widget_json: Record<string, unknown> | null;
  activity_log: Record<string, unknown> | Array<Record<string, unknown>> | null;
  token_count: number | null;
  created_at: string;
}

export interface ActiveSession {
  id: string;
  project_id: string;
  title: string | null;
  model: string;
  created_at: string;
  is_new: boolean;
}

export interface GitHubInstallation {
  id: string;
  installation_id: number;
  account_login: string;
  account_type: string;
  suspended_at: string | null;
}

export interface GitHubRepo {
  id: number;
  full_name: string;
  default_branch: string;
  private: boolean;
}

export interface GitHubStatus {
  connected: boolean;
  installations: GitHubInstallation[];
}

export interface ProjectRepoResponse {
  id: string;
  project_id: string;
  github_repo_id: number;
  repo_full_name: string;
  default_branch: string;
}

export const api = {
  auth: {
    login(email: string, password: string) {
      return request<TokenResponse>("/api/v1/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password }),
      });
    },
    register(email: string, password: string, display_name?: string) {
      return request<TokenResponse>("/api/v1/auth/register", {
        method: "POST",
        body: JSON.stringify({ email, password, display_name }),
      });
    },
    me() {
      return request<UserResponse>("/api/v1/auth/me");
    },
  },

  github: {
    status() {
      return request<GitHubStatus>("/api/v1/github/status/");
    },
    installations() {
      return request<GitHubInstallation[]>("/api/v1/github/installations/");
    },
    repos(installationId: string) {
      return request<GitHubRepo[]>(
        `/api/v1/github/installations/${installationId}/repos/`,
      );
    },
    disconnect(installationId: string) {
      return request<void>(
        `/api/v1/github/installations/${installationId}`,
        { method: "DELETE" },
      );
    },
    oauthUrl() {
      return `${API_BASE}/auth/github?token=${accessToken}`;
    },
  },

  projects: {
    list() {
      return request<Project[]>("/api/v1/projects/");
    },
    create(name: string, description?: string) {
      return request<Project>("/api/v1/projects/", {
        method: "POST",
        body: JSON.stringify({ name, description }),
      });
    },
    get(id: string) {
      return request<Project>(`/api/v1/projects/${id}`);
    },
    messages(projectId: string, opts?: { limit?: number; before?: string }) {
      const params = new URLSearchParams();
      if (opts?.limit) params.set("limit", String(opts.limit));
      if (opts?.before) params.set("before", opts.before);
      const qs = params.toString();
      return request<ProjectMessage[]>(
        `/api/v1/projects/${projectId}/messages${qs ? `?${qs}` : ""}`,
      );
    },
    activeSession(projectId: string) {
      return request<ActiveSession>(`/api/v1/projects/${projectId}/active-session`);
    },
    connectRepo(
      projectId: string,
      body: {
        installation_id: string;
        github_repo_id: number;
        repo_full_name: string;
        default_branch: string;
      },
    ) {
      return request<ProjectRepoResponse>(
        `/api/v1/projects/${projectId}/repo`,
        { method: "POST", body: JSON.stringify(body) },
      );
    },
    disconnectRepo(projectId: string) {
      return request<void>(`/api/v1/projects/${projectId}/repo`, {
        method: "DELETE",
      });
    },
  },

  sessions: {
    list(projectId: string) {
      return request<Session[]>(`/api/v1/projects/${projectId}/sessions/`);
    },
    create(projectId: string, title?: string) {
      return request<Session>(`/api/v1/projects/${projectId}/sessions/`, {
        method: "POST",
        body: JSON.stringify({ title }),
      });
    },
    get(projectId: string, sessionId: string) {
      return request<SessionDetail>(
        `/api/v1/projects/${projectId}/sessions/${sessionId}`,
      );
    },
  },
};
